/**
 * Client-side runtime estimates, mirroring the engine's bucket model. Used
 * for zone-card hints and the Settings window-capacity warning; the real
 * plan varies with weather.
 */

import type { ScheduleConfig, Zone } from '@irrigation/core';
import { allowedDepletionIn, totalAvailableWaterIn } from '@irrigation/core';

/**
 * Rough per-session estimate from the zone's own parameters: refill depth at
 * full allowed depletion, and the runtime to apply it. Uses the core bucket
 * helpers so drip zones (whose reservoir is scaled by the wetted fraction) are
 * sized identically to the backend.
 */
export function sessionEstimate(z: Zone): { grossIn: number; minutes: number } | null {
  if (z.efficiency <= 0 || z.precipRateInPerHr <= 0) return null;
  const netIn = allowedDepletionIn(z);
  if (netIn <= 0) return null;
  const grossIn = netIn / z.efficiency;
  return { grossIn, minutes: (grossIn / z.precipRateInPerHr) * 60 };
}

/**
 * Upper bound on a calibration run: the tighter of the per-zone runtime cap
 * and the per-zone daily-water budget. Both the recommended default and a
 * user-supplied override are clamped to this. Mirrors the backend
 * `calibrationRuntimeCap`.
 */
export function calibrationRuntimeCap(z: Zone, config: ScheduleConfig): number {
  const dailyCapMinutes =
    config.maxDailyWaterIn > 0 && z.precipRateInPerHr > 0
      ? (config.maxDailyWaterIn / z.precipRateInPerHr) * 60
      : Infinity;
  return Math.min(config.maxZoneRuntimeMinutes, dailyCapMinutes);
}

/**
 * Client-side mirror of the server's Calibrate run duration: a full managed
 * root-zone refill ({@link totalAvailableWaterIn}: AWC × rootDepth × wetted
 * fraction, grossed up for efficiency) — enough to push the soil past field
 * capacity so its drainage plateau is observable — floored at 5 min and capped
 * by {@link calibrationRuntimeCap}. Shares the core TAW helper with the backend
 * `calibrationRunMinutes` so the zone-card estimate and the real run can't
 * drift apart. This is the recommended default; the user may override it per run.
 */
export function calibrationRunMinutes(z: Zone, config: ScheduleConfig): number {
  if (z.precipRateInPerHr <= 0 || z.efficiency <= 0) return 5;
  const grossIn = totalAvailableWaterIn(z) / z.efficiency;
  const raw = Math.round((grossIn / z.precipRateInPerHr) * 60);
  return Math.max(5, Math.min(calibrationRuntimeCap(z, config), raw));
}

/**
 * Worst-case valve minutes the enabled zones can need in a single day (every
 * zone due at once), after the safety caps. Compared against the watering
 * window to warn about deferrals.
 */
export function dailyDemandMinutes(zones: Zone[], config: ScheduleConfig): number {
  return zones
    .filter((z) => z.enabled)
    .reduce((sum, z) => {
      let minutes: number;
      if (z.scheduleMode === 'FIXED' && z.fixedSchedule) {
        minutes = z.fixedSchedule.runMinutes;
      } else if (z.fixedRuntimeMinutes) {
        minutes = z.fixedRuntimeMinutes;
      } else {
        const est = sessionEstimate(z);
        if (!est) return sum;
        minutes = est.minutes;
      }
      minutes = Math.min(minutes, config.maxZoneRuntimeMinutes);
      if (z.precipRateInPerHr > 0 && config.maxDailyWaterIn > 0) {
        minutes = Math.min(minutes, (config.maxDailyWaterIn / z.precipRateInPerHr) * 60);
      }
      return sum + minutes;
    }, 0);
}
